/**
 * Wohin ein Klick auf einen Kalendereintrag führt (Spec 07, „Ein Eintrag":
 * Tabelle `kind` → Ziel, AK-466). Reine Funktion statt Verzweigung in
 * `CalendarEntryRow.tsx`, damit sie ohne Rendern geprüft werden kann —
 * `apps/web` hat keine Render-Test-Infrastruktur (nur E2E), eine reine
 * Funktion bleibt trotzdem mit Vitest fassbar.
 *
 * Das Ziel richtet sich nach `kind`, nicht nach `subject === null` (Befund
 * „Anmerkung 1" des Gatekeepers, `docs/work/007-kalender-af4593/
 * findings.md`, Tor 2): Seit der Anker-Korrektur (K1) filtert Sichtbarkeit
 * über die Vertrags- bzw. Aufgaben-Entity, nicht mehr über das Subjekt
 * selbst — `subject` kann deshalb **maskiert** sein, obwohl der Eintrag
 * selbst sichtbar ist. `subject === null` bedeutet jetzt zweierlei:
 *
 * 1. **Gebündelte Mietfälligkeit** (`kind = 'charge'`, `count > 1`, AK-450) —
 *    kein Objekt *by design*, führt in den Bereich *Geld* (kein Leck: die
 *    Fläche nennt kein konkretes Objekt).
 * 2. **Maskiertes Subjekt** — jede andere Kombination mit `subject === null`.
 *    Ein Grant direkt auf die Vertrags- bzw. Aufgaben-Entity macht den
 *    Eintrag sichtbar, ohne dass der Betrachter das Subjekt selbst sehen
 *    darf. Kein Ziel: Auf ein Objekt zu verweisen, das der Betrachter nicht
 *    sehen darf, endet in 403 oder einer leeren Ansicht — nicht klickbar ist
 *    ehrlicher und verrät nichts über die Existenz des maskierten Objekts.
 *
 * Ist `subject` sichtbar, führt ein Termin oder eine Frist
 * (`appointment`/`deadline`) auf den Reiter *Termine*, eine Sollstellung auf
 * *Geld* — jeweils am Objekt. Eine **Aufgabe** öffnet seit Spec 45 ihr Sheet
 * (`type: 'task'`, Regel 11), unabhängig vom Subjekt.
 */

import type { CalendarEntry } from './api'

export type CalendarEntryTarget =
  | { readonly type: 'object'; readonly entityId: string; readonly tab: 'termine' | 'geld' }
  | { readonly type: 'task'; readonly ticketId: string }
  | { readonly type: 'contract'; readonly contractId: string }
  | { readonly type: 'geld' }
  | { readonly type: 'none' }

const OBJECT_TAB_BY_KIND: Record<string, 'termine' | 'geld'> = {
  appointment: 'termine',
  deadline: 'termine',
  charge: 'geld',
}

export function calendarEntryTarget(
  entry: Pick<CalendarEntry, 'kind' | 'subject' | 'count' | 'source_id'>,
): CalendarEntryTarget {
  // Spec 45, Regel 11: Eine Aufgabe öffnet `task-sheet`, nicht den Objektreiter —
  // auch mit maskiertem Subjekt: Das Sheet lädt `GET /tickets/{source_id}`
  // (sichtbar über die Aufgabe selbst), es verweist auf kein Objekt.
  if (entry.kind.value === 'task' && entry.source_id !== null && entry.source_id !== undefined) {
    return { type: 'task', ticketId: entry.source_id }
  }

  if (entry.subject === null || entry.subject === undefined) {
    if (entry.kind.value === 'charge' && entry.count > 1) {
      return { type: 'geld' }
    }
    return { type: 'none' }
  }

  if (entry.subject.kind.value === 'contract') {
    return { type: 'contract', contractId: entry.subject.id }
  }

  return {
    type: 'object',
    entityId: entry.subject.id,
    tab: OBJECT_TAB_BY_KIND[entry.kind.value] ?? 'termine',
  }
}
